import find from 'lodash/find';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import { connect } from '~/redux/connect';
import cn from 'classnames';

import {QrScanner} from '~/ui/QrScanner/QrScanner';
import MessageList from "~/ui/components/MessageList/MessageList";
import {
    getAllResultParams,
    getCustomActionResult,
    getGlobalActions,
    getGroups,
    getModelName,
    getModelTitle,
    getModelTooltip,
    getResultsValue,
    getSelectThroughout,
    getSelectedRows,
    getPageAction,
} from "~/stateGetter";
import {presentActionInfo} from "~/statePresenter";
import {performCustomAction, performCustomScanQrAction} from "~/rx/model/customAction";
import stripTags from 'striptags';
import ItemGrid from '~/ui/ItemGrid';
import identify from "~/identify";
import css from "./css.css";
import ElementTooltipContainer from "~/ui/Tooltip/ElementTooltipContainer";
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import memoize from "lodash/memoize";

import { SpinnerButton as Button, Dialog, Divider, HStack, IconMenu, MenuItem, Text, Tooltip, VStack } from "~/native-ui";
import { ConfirmField } from './ConfirmField';

const ActionItem = (action) => {
    let tooltip = action.tooltip;

    let captionItem =  tooltip && tooltip ?
        <ElementTooltipContainer leftMargin tooltip={tooltip}>{action.caption}</ElementTooltipContainer> :
        action.caption;

    return (<MenuItem
        icon={action.icon}
        iconFamily={action.iconFamily}
        caption={captionItem}
        value={action.value}
        onClick={action.onClick}
        disabled={action.disabled}
    />)
};


const ActionButton = (props) => {
    const icon = props.icon,
          iconFamily = props.iconFamily,
          caption = props.caption,
          button = (
                <Button
                    primary
                    iconSize="sm"
                    key={props.value}
                    icon={icon}
                    iconFamily={iconFamily}
                    label={icon ? "" : caption}
                    onPress={props.onClick}
                    disabled={props.disabled}
                />
            )

    if (!icon) {
        return button;
    }
    
    return (
        <Tooltip tooltip={caption} disabled={props.disabled} >{ button }</Tooltip>
    )
};

export class ActionMenu extends Component {
    static propTypes = {
        actions: PropTypes.array.isRequired,
        activeAction: PropTypes.object,
        isActionActive: PropTypes.bool.isRequired,
        title: PropTypes.string,
        otherItems: PropTypes.array.isRequired,
        disabled: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        isActionActive: false,
        otherItems: [],
        disabled: false
    };

    state  = {
        confirmAction: null,
        showDialog: false,
        criticalConfirmValid: false,
    };

    get disablePrimaryConfirm () {
        return !this.state.criticalConfirmValid;
    }

    componentDidMount() {
        this.autoOpenModel()
    }

    componentDidUpdate(prevProps) {
        if (this.props.modelAction !== prevProps.modelAction) {
            this.autoOpenModel()
        }
    }

    autoOpenModel = () => {
        const { modelAction, actions } = this.props;
        if (modelAction?.action) {
            const action = find(actions, {value: modelAction.action})
            this.performAction(action);
        }
    }

    hideConfirmActionDialog = () => {
        this.setState({showDialog: false});
    };

    setActionDialog = (actionName, criticalConfirm) => {
        this.setState({ confirmAction: actionName, showDialog: true, criticalConfirmValid: !criticalConfirm });
    };


    performAction = async (action) => {
        if (!action) {
            return;
        }

        const shouldConfirmAction = action.confirmation === true
            || action.requiresSelection && action.confirmation <= this.props.selectedRowCount
            || action.criticalConfirmText
            || action.grid
            || action.scanQr
            || this.props.modelAction?.action === action.value;

        if (shouldConfirmAction) {
            return this.setActionDialog(action.value);
        }

        action.action(action.value);
    }

    onChangeCriticalConfirm = (isMatch) => {
        this.setState({ criticalConfirmValid: isMatch });
    }

    prepareAction = (theActions, action) => {
        if(action.disabled) { return theActions; }
        

        theActions.push({
            ...action,
            onClick: async () => this.performAction(action)
        });

        return theActions;
    };
    
    renderDialog = () => {
        let confirmedActionName = this.state.confirmAction;
        if(!confirmedActionName || !confirmedActionName.length) {
            return;
        }
            
        let confirmedAction = find(this.props.actions, {value: confirmedActionName});
        
        if(confirmedAction.scanQr) {
            return this.renderQrCodeDialog(confirmedAction);
        }

        if(confirmedAction.grid) {
            return this.renderActionGrid(confirmedAction);
        }

        if(confirmedAction.requiresSelection) {
            return this.renderActionRequiresSelectionDialog(confirmedAction);    
        }
        
        return this.renderActionConfirmDialog(confirmedAction);
    };

    renderBaseDialog = (children, { translate = {}, showConfirmActions = true, showCancel = true, showConfirm = true, } = {}) => {
        let { trans, title } = this.props;

        let confirmedActionName = this.state.confirmAction,
            showDialog = this.state.showDialog,
            confirmedAction = find(this.props.actions, {value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title),
        };

        let confirmTitle = confirmedAction.dialogTitle ?
            confirmedAction.dialogTitle :
            trans('confirmAction.title', transData);

        const confirmActions =  !showConfirmActions ? null : [
            {
                label: translate.okay ?? actionTitle,
                disabled: this.disablePrimaryConfirm,
                onClick: () => {
                    confirmedAction.action(confirmedActionName);
                    this.hideConfirmActionDialog();
                }
            },
            !showCancel ? null : {
                label: translate.no ?? trans('no'),
                onClick: this.hideConfirmActionDialog
            }
        ].filter(Boolean);

        return (
            <Dialog
                className={css.dialog}
                actions={confirmActions}
                active={showDialog}
                title={confirmTitle}
                onOverlayClick={this.hideConfirmActionDialog}
                onEscKeyDown={this.hideConfirmActionDialog}
            >
                {children}
            </Dialog>
        )
    };

    renderQrCodeDialog(confirmedAction) {
        const scannerProps = {
            modelId: this.props.modelId,
            onScan: async (qr_code_string) => {
                if (await confirmedAction.action(this.state.confirmAction, { qr_code_string })) {
                    this.hideConfirmActionDialog()
                }
            }
        }

        return this.renderBaseDialog(<QrScanner { ...scannerProps} />, { showConfirm: false })
    }

    renderActionGrid = (confirmedAction) => {
        let requiresSelectionMessage = null,
            requiresSelection = confirmedAction.requiresSelection,
            { trans, rowCount, modelTitle } = this.props;

        if (requiresSelection) {
            const transData = {
                count: rowCount,
                modelNamePlural: modelTitle
            }

            const messagesListProps = {
                messages: [
                    {message: trans('requiresSelection.message', transData), type: "warning"}
                ],
                level: 0,

            }

            requiresSelectionMessage = <MessageList { ...messagesListProps} />
        }

        return this.renderBaseDialog(
            <React.Fragment>
                { requiresSelectionMessage }
                <ItemGrid
                    modelId={this.props.modelId}
                    gridName={confirmedAction.grid}
                    view={identify.VIEW_EDIT}
                    className={css.itemGrid}
                />
            </React.Fragment>
        );
    };

    renderConfirmField (action) {
        const { criticalConfirmText, criticalConfirmQuantity = 0 } = action;
        if (!criticalConfirmText) {
            return null;
        }

        return (
            <ConfirmField
                modelId={this.props.modelId}
                threshold={criticalConfirmQuantity || 0}
                onChange={this.onChangeCriticalConfirm}
                valueToConfirm={action.criticalConfirmText}
            />
        )
    }

    renderActionRequiresSelectionDialog = (action) => {
        let { trans, title, rowCount, modelTitle } = this.props;

        const actionProps = {
            count: rowCount,
            modelNamePlural: modelTitle
        }

        let transData = {
                actionTitle: trans('requiresSelection.title', actionProps),
                itemTitle: stripTags(title),
                ...actionProps,
            },
            confirmMessage = trans('requiresSelection.message', transData)

        return this.renderBaseDialog((
            <VStack space="2">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ), {
            translate: {
                okay: transData.actionTitle
            }
        })
    };



    renderActionConfirmDialog = (action) => {
        let { trans, title } = this.props;

        let confirmedActionName = this.state.confirmAction,
            confirmedAction = find(this.props.actions, {value: confirmedActionName}),
            actionTitle = get(confirmedAction, 'caption');

        let transData = {
            actionTitle,
            itemTitle: stripTags(title)
        };

        let confirmMessage = trans(get(confirmedAction, 'confirmationMessage', 'confirmAction.message'), transData);

        return this.renderBaseDialog((
            <VStack space="2" className="test">
                <Text>{confirmMessage}</Text>
                {this.renderConfirmField(action)}
            </VStack>
        ))
    };


    getButtonProps = memoize((disabled) => ({ disabled: disabled, primary: true, iconSize: "sm" }))

    renderMenuGroup = ([group, groupActions]) => {
        let { activeGroup, activeAction, isActionActive, className, menuClassName, disabledClassName, } = this.props,
            iconClassName = cn(menuClassName, {[disabledClassName]: isActionActive}),
            isActiveGroup = !!isActionActive && activeGroup?.name === group,
            icon = activeAction && isActiveGroup ? 'spinning' : get(groupActions, "0.group.icon", 'more-vert'),
            iconFamily = !activeAction ? get(groupActions, "0.group.iconFamily") : undefined ;
            
            if (groupActions.length <= 0) {
            return null
        }

        if (groupActions.length === 1) {
            let action = groupActions[0];
            action = {
                ...action,
                icon,
                iconFamily,
                disabled: action.disabled || this.props.disabled,
                key: action.value,
                fontSize: "xs"
            };

            return <ActionButton { ...action } />
        }


        return (
                <IconMenu
                    aria-description={get(groupActions, "0.group.title", group)}
                    tooltip={get(groupActions, "0.group.title", group)}
                    key={get(groupActions, "0.group.title", group)}
                    icon={icon}
                    iconFamily={iconFamily}
                    iconClassName={iconClassName}
                    buttonProps={this.getButtonProps(this.props.disabled)}
                >
                    {groupActions.map((props) => <ActionItem {...props} key={props.value} />)}
                </IconMenu>
            )
    }


    renderMenu () {
        let { actions, activeAction, otherItems, trans } = this.props;

        actions = actions.reduce(this.prepareAction, []);

        const label = activeAction ? activeAction.caption : trans('actions');

        let groupedActions = groupBy(actions, "groupName");
        const flatActions = get(groupedActions, "", []);
        const defaultGroup = get(groupedActions, "undefined", []);

        groupedActions = omit(groupedActions, "undefined", "");

        groupedActions = Object.entries(groupedActions).map(this.renderMenuGroup)

        const actionButtonsCount = flatActions.length + groupedActions.length + (otherItems?.length ?? 0);

        if (actionButtonsCount.length < 1) {
            return null;
        }



        return (
            <React.Fragment>
                <React.Fragment>{ !this.props.noDivider && <Divider orientation="vertical" bg="coolGray.300" h="5" mx="2" />}</React.Fragment>
                <HStack space={2} alignItems="center">
                    {groupedActions}
                    {
                        flatActions.map((props) => <ActionButton { ...props } fontSize="xs" disabled={props.disabled || this.props.disabled} key={props.value} />)
                    }
                    {otherItems}

                    {this.renderMenuGroup([undefined, defaultGroup])}
                </HStack>
            </React.Fragment>
        )
    }

    render() {
        let { actions, className, } = this.props;
        
        return (
            <React.Fragment>
                {this.renderDialog()}
                <React.Fragment>{actions.length ? this.renderMenu() : null}</React.Fragment>
            </React.Fragment>
        );
    }
}

export const TableActionMenu = connect((state, {modelId}) => {
    return {
        modelAction: getPageAction(state, modelId),
        modelName: getModelName(state, modelId),
        modelTitle: getModelTitle(state, modelId),
        actions: getGlobalActions(state, modelId),
        rowCount: getResultsValue(state, modelId).total,
        customActionResult: getCustomActionResult(state, modelId),
        tooltip:  getModelTooltip(state, modelId, 'global_actions'),
        selectedThroughout: getSelectThroughout(state, modelId),
        selectedRows: getSelectedRows(state, modelId),
        groups: getGroups(state, modelId),
        ...getAllResultParams(state, modelId),
    }
}, (dispatch, {modelId}) => {
    return {
        executeAction: (actionName) => dispatch(performCustomAction(modelId, actionName)),
        executeScanQrAction: (actionName, data) => dispatch(performCustomScanQrAction(modelId, actionName, data)),
    }
}, ({groups, actions, customActionResult, tooltip, selectedThroughout, selectedRows, rowCount, modelTitle, modelAction}, {executeAction, executeScanQrAction}, {...otherOwnProps}) => {
    return {
        tooltip,
        rowCount,
        modelTitle,
        modelAction,
        selectedRowCount: selectedRows.length,
        ...otherOwnProps,
        ...presentActionInfo({
            groups, actions, executeAction, executeScanQrAction, actionResult: customActionResult,
            selectedRows: !selectedThroughout ? selectedRows : [true]
        })
    }
})(ActionMenu);